import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, FlagIcon, PinIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { Seo } from '../../components/Seo';
import { Dialog } from '../../components/portal/Dialog';
import { JoinDialog } from '../../components/academy/JoinDialog';
import { UpvoteButton } from '../../components/academy/UpvoteButton';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import { initials, timeAgo } from '../../utils/academyFormat';
import type { ForumAuthor, ForumReply } from '../../types/academyHub';
import { NotFound } from '../NotFound';
const FLAG_REASONS = ['Spam or advertising', 'Harassment or abuse', 'Off-topic', 'Misleading information', 'Personal data shared'];
export function AcademyThread() {
  const {
    channel: channelSlug,
    threadId
  } = useParams<{
    channel: string;
    threadId: string;
  }>();
  const {
    state
  } = useAcademy();
  const {
    hub,
    role,
    addReply,
    flagPost,
    togglePin,
    channels
  } = useAcademyHub();
  const [draft, setDraft] = React.useState('');
  const [replyTo, setReplyTo] = React.useState<ForumReply | null>(null);
  const [flagging, setFlagging] = React.useState<{
    type: 'thread' | 'reply';
    id: string;
  } | null>(null);
  const [reason, setReason] = React.useState(FLAG_REASONS[0]);
  const [flagged, setFlagged] = React.useState<string[]>([]);
  const [joinOpen, setJoinOpen] = React.useState(false);
  const composerRef = React.useRef<HTMLTextAreaElement>(null);
  const thread = hub.threads.find(t => t.id === threadId);
  const channel = channels.find(c => c.slug === channelSlug);
  if (!thread || !channel || thread.hidden) return <NotFound data-id="element-2013" data-mp="bohbphx88hrm" />;
  const replies = hub.replies.filter(r => r.threadId === thread.id && !r.hidden);
  const topLevel = replies.filter(r => !r.parentId).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const childrenOf = (id: string) => replies.filter(r => r.parentId === id).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!state.learner) {
      setJoinOpen(true);
      return;
    }
    if (draft.trim().length < 2) return;
    addReply(thread.id, draft, replyTo ? replyTo.parentId ?? replyTo.id : undefined);
    setDraft('');
    setReplyTo(null);
  };
  const startReply = (reply: ForumReply) => {
    setReplyTo(reply);
    composerRef.current?.focus();
  };
  const confirmFlag = () => {
    if (!flagging) return;
    flagPost(flagging.type, flagging.id, reason);
    setFlagged(f => [...f, flagging.id]);
    setFlagging(null);
  };
  const flagButton = (type: 'thread' | 'reply', id: string) => flagged.includes(id) ? <span className="text-small text-muted" data-id="element-2014" data-mp="zodrygw6fmzu">Reported</span> : <button type="button" onClick={() => state.learner ? setFlagging({
    type,
    id
  }) : setJoinOpen(true)} className="inline-flex items-center gap-1 text-small text-muted hover:text-ink" data-id="element-2015" data-mp="14hgkclp7wqeg">
        <FlagIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2016" data-mp="19t3uanvhst4l" />
        Report
      </button>;
  return <>
      <Seo title={`${thread.title}, Cavalla Academy community`} description={thread.body.slice(0, 150)} path={`/academy/community/${channel.slug}/${thread.id}`} noIndex data-id="element-2017" data-mp="1nn4kgt8dp7tc" />
      <Container className="max-w-[860px] py-10 sm:py-14" data-id="element-2018" data-mp="ux4ip396caup">
        <nav aria-label="Breadcrumb" data-id="element-2019" data-mp="qv9fft7onnea">
          <ol className="flex flex-wrap items-center gap-2 text-small text-muted" data-id="element-2020" data-mp="qwc55xt9rjsm">
            <li data-id="element-2021" data-mp="14vk5iu164xye">
              <Link to="/academy/community" className="hover:text-ink" data-id="element-2022" data-mp="14w3p0f6gvuej">
                Community
              </Link>
            </li>
            <li aria-hidden="true" data-id="element-2023" data-mp="2g9pdrz8eztzj">
              <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-2024" data-mp="2g8wchds85wos" />
            </li>
            <li data-id="element-2025" data-mp="2bauw50m6obh0">
              <Link to={`/academy/community/${channel.slug}`} className="hover:text-ink" data-id="element-2026" data-mp="2bbe4pqpy1nid">
                {channel.name}
              </Link>
            </li>
          </ol>
        </nav>

        <article className="mt-6 flex gap-5" data-id="element-2027" data-mp="psc4sk6iuggd">
          <UpvoteButton postId={thread.id} count={thread.upvotes} data-id="element-2028" data-mp="pt3vbgl9hnzs" />
          <div className="min-w-0 flex-1" data-id="element-2029" data-mp="urf5jsfgy0ij">
            <h1 className="font-display text-[26px] leading-tight text-ink sm:text-[32px]" data-id="element-2030" data-mp="utrni5l88avl">{thread.title}</h1>
            <AuthorLine author={thread.author} at={thread.createdAt} data-id="element-2031" data-mp="yqxgpwhhmx86" />
            <PostBody text={thread.body} className="mt-5 text-[16px]" data-id="element-2032" data-mp="13rttkzjthtpq" />
            <div className="mt-5 flex items-center gap-5" data-id="element-2033" data-mp="18qob7y61tc89">
              {flagButton('thread', thread.id)}
              {role !== 'learner' && <button type="button" onClick={() => togglePin(thread.id)} className="inline-flex items-center gap-1 text-small text-muted hover:text-ink" data-id="element-2034" data-mp="1dk9b5vr7cga3">
                  <PinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2035" data-mp="1mjw2foq7w5pf" />
                  {thread.pinned ? 'Unpin' : 'Pin to top'}
                </button>}
            </div>
          </div>
        </article>

        <section aria-labelledby="replies" className="mt-12 border-t border-line pt-8" data-id="element-2036" data-mp="3c1jjcx44gn8h">
          <h2 id="replies" className="text-[15px] font-medium text-ink" data-id="element-2037" data-mp="37040nbpf2st9">
            {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
          </h2>
          <ol className="mt-4 divide-y divide-line" data-id="element-2038" data-mp="2y0gy2mkybgym">
            {topLevel.map(reply => <li key={reply.id} className="py-5" data-id="element-2039" data-mp="3uq80d7zhi8ez">
                <ReplyItem reply={reply} onReply={() => startReply(reply)} flag={flagButton('reply', reply.id)} data-id="element-2040" data-mp="3upy6vaf8dj4j" />
                {childrenOf(reply.id).length > 0 && <ol className="mt-4 space-y-4 border-l border-line pl-5 sm:ml-4" data-id="element-2041" data-mp="3lxcwoipo0krb">
                    {childrenOf(reply.id).map(child => <li key={child.id} data-id="element-2042" data-mp="3gwyuvienq71q">
                        <ReplyItem reply={child} onReply={() => startReply(child)} flag={flagButton('reply', child.id)} data-id="element-2043" data-mp="i9b569yud6ya" />
                      </li>)}
                  </ol>}
              </li>)}
          </ol>

          <form onSubmit={submit} className="mt-6 rounded-md border border-line bg-panel p-5" data-id="element-2044" data-mp="wgo5z0pc2w97">
            {replyTo && <p className="mb-3 flex items-center justify-between gap-3 text-small text-muted" data-id="element-2045" data-mp="nmj9a0niqqlr">
                <span data-id="element-2046" data-mp="rnmpwpde4qo1">Replying to {replyTo.author.name}</span>
                <button type="button" onClick={() => setReplyTo(null)} className="text-royal hover:text-ink" data-id="element-2047" data-mp="rm1w51ywhbr8">
                  Cancel
                </button>
              </p>}
            <label htmlFor="reply" className="text-[14px] font-medium text-ink" data-id="element-2048" data-mp="iorhd6mvj4hk">
              Your reply
            </label>
            <textarea id="reply" ref={composerRef} rows={4} value={draft} onChange={e => setDraft(e.target.value)} className={`${inputClass} mt-2`} placeholder={state.learner ? 'Share what worked for you' : 'Create a free account to reply'} data-id="element-2049" data-mp="4prtw2b3kvmz" />
            <div className="mt-3 flex justify-end" data-id="element-2050" data-mp="3nm3xckdnsk5b">
              <Button type="submit" disabled={Boolean(state.learner) && draft.trim().length < 2} data-id="element-2051" data-mp="3jjh4qh95yv72">
                {state.learner ? 'Post reply' : 'Create account to reply'}
              </Button>
            </div>
          </form>
        </section>
      </Container>

      <Dialog open={Boolean(flagging)} onClose={() => setFlagging(null)} title="Report this post" description="A moderator will review it. The author is not told who reported it." data-id="element-2052" data-mp="3sim4fqwgoukt">
        <Field id="flag-reason" label="Reason" data-id="element-2053" data-mp="63sw5vjbmo3d">
          <select id="flag-reason" value={reason} onChange={e => setReason(e.target.value)} className={inputClass} data-id="element-2054" data-mp="k4dfqtx28nxx">
            {FLAG_REASONS.map(r => <option key={r} data-id="element-2055" data-mp="f5seqs9zi08f">{r}</option>)}
          </select>
        </Field>
        <div className="mt-6 flex justify-end gap-3" data-id="element-2056" data-mp="2lwi3rubfv13d">
          <Button type="button" variant="secondary" onClick={() => setFlagging(null)} data-id="element-2057" data-mp="2gwnf7xf4ynlh">
            Cancel
          </Button>
          <Button type="button" onClick={confirmFlag} data-id="element-2058" data-mp="1w1r822w072vu">
            Send report
          </Button>
        </div>
      </Dialog>
      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoined={() => setJoinOpen(false)} data-id="element-2059" data-mp="2018bkqrvdpyd" />
    </>;
}
function ReplyItem({
  reply,
  onReply,
  flag
}: {
  reply: ForumReply;
  onReply: () => void;
  flag: React.ReactNode;
}) {
  return <div className="flex gap-4" data-id="element-2060" data-mp="1d23jfk2aawtb">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-medium ${reply.author.staff ? 'bg-gold text-charcoal' : 'bg-charcoal text-ink'}`} aria-hidden="true" data-id="element-2061" data-mp="1d355skor35qc">
        {initials(reply.author.name)}
      </span>
      <div className="min-w-0 flex-1" data-id="element-2062" data-mp="1r2wec5whztwy">
        <AuthorLine author={reply.author} at={reply.createdAt} data-id="element-2063" data-mp="1qzs1yl8vprv7" />
        <PostBody text={reply.body} className="mt-2 text-[15px]" data-id="element-2064" data-mp="2xgvrtm26gnbs" />
        <div className="mt-3 flex items-center gap-5" data-id="element-2065" data-mp="32gru9gv886p8">
          <UpvoteButton postId={reply.id} count={reply.upvotes} compact data-id="element-2066" data-mp="32f6zpr8wwnrd" />
          <button type="button" onClick={onReply} className="text-small text-royal hover:text-ink" data-id="element-2067" data-mp="qsn2451ecxht">
            Reply
          </button>
          {flag}
        </div>
      </div>
    </div>;
}
function AuthorLine({
  author,
  at
}: {
  author: ForumAuthor;
  at: string;
}) {
  return <p className="mt-2 flex flex-wrap items-center gap-x-2 text-small text-muted" data-id="element-2068" data-mp="c5blvpu9spbd">
      <span className={author.staff ? 'font-medium text-royal' : 'font-medium text-ink'} data-id="element-2069" data-mp="82x7bhxmwbpw">{author.name}</span>
      {author.staff && <span className="rounded-sm bg-charcoal px-1.5 py-0.5 text-[11px] text-royal" data-id="element-2070" data-mp="332f9b24hg39">Academy team</span>}
      {author.detail && <span data-id="element-2071" data-mp="3uax19b3qhg2p">{author.detail}</span>}
      <span aria-hidden="true" data-id="element-2072" data-mp="3pbbr04j45lao">·</span>
      <span data-id="element-2073" data-mp="3pcdfbap544ac">{timeAgo(at)}</span>
    </p>;
}
function PostBody({
  text,
  className = ''
}: {
  text: string;
  className?: string;
}) {
  return <div className={`space-y-3 leading-relaxed text-body ${className}`} data-id="element-2074" data-mp="1sgxrwva698sn">
      {text.split(/\n{2,}/).map((para, i) => <p key={i} className="whitespace-pre-line" data-id="element-2075" data-mp="v0q287kzgrnd" data-mpi={i}>
          {para}
        </p>)}
    </div>;
}